<template>
  <b-table
    v-click-outside="handleClickOut"
    v-bind="{ ...$props, ...$attrs }"
    v-on="handleListeners($listeners)"
    :items="tableItems"
  >
    <template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope"
      ><slot :name="slot" v-bind="scope"
    /></template>
    <template v-for="(field, index) in fields" #[`cell(${field.key})`]="data">
      <b-form-datepicker
        @keydown.native="handleKeydown($event, index, data)"
        @input="(value) => inputHandler(value, data, field.key)"
        v-bind="{ ...field }"
        v-focus="'date'"
        v-if="
          field.type === 'date' &&
          tableItems[data.index].isEdit &&
          selectedCell === field.key &&
          field.editable
        "
        :key="index"
        :type="field.type"
        :value="tableItems[data.index][field.key]"
      ></b-form-datepicker>
      <b-form-select
        @keydown.native="handleKeydown($event, index, data)"
        @change="(value) => inputHandler(value, data, field.key, field.options)"
        v-bind="{ ...field }"
        v-focus
        v-else-if="
          field.type === 'select' &&
          tableItems[data.index].isEdit &&
          selectedCell === field.key &&
          field.editable
        "
        :key="index"
        :value="tableItems[data.index][field.key]"
      ></b-form-select>
      <b-form-checkbox
        @keydown.native="handleKeydown($event, index, data)"
        @change="(value) => inputHandler(value, data, field.key)"
        v-bind="{ ...field }"
        v-focus="'checkbox'"
        v-else-if="
          field.type === 'checkbox' &&
          tableItems[data.index].isEdit &&
          selectedCell === field.key &&
          field.editable
        "
        :key="index"
        :checked="tableItems[data.index][field.key]"
      ></b-form-checkbox>
      <b-form-rating
        @keydown="handleKeydown($event, index, data)"
        @change="(value) => inputHandler(value, data, field.key)"
        v-bind="{ ...field }"
        v-focus
        v-else-if="
          field.type === 'rating' &&
          field.type &&
          tableItems[data.index].isEdit &&
          selectedCell === field.key &&
          field.editable
        "
        :key="index"
        :type="field.type"
        :value="tableItems[data.index][field.key]"
      ></b-form-rating>
      <b-form-input
        @keydown="handleKeydown($event, index, data)"
        @input="(value) => inputHandler(value, data, field.key)"
        v-bind="{ ...field }"
        v-focus
        v-else-if="
          field.type &&
          tableItems[data.index].isEdit &&
          selectedCell === field.key &&
          field.editable
        "
        :key="index"
        :type="field.type"
        :value="tableItems[data.index][field.key]"
      ></b-form-input>
      <div
        class="data-cell"
        @click="handleEditCell($event, data.index, field.key)"
        v-else
        :key="index"
      >
        <slot
          v-if="$scopedSlots[`cell(${field.key})`]"
          :name="`cell(${field.key})`"
          v-bind="data"
        ></slot>
        <template v-else>{{ getValue(data, field) }}</template>
      </div>
    </template>
  </b-table>
</template>

<script>
import {
  BTable,
  BFormDatepicker,
  BFormInput,
  BFormSelect,
  BFormCheckbox,
  BFormRating
} from 'bootstrap-vue'
import Vue from 'vue'

export default Vue.extend({
  name: 'BEditableTable',
  components: {
    BTable,
    BFormDatepicker,
    BFormInput,
    BFormSelect,
    BFormCheckbox,
    BFormRating
  },
  props: {
    fields: Array,
    items: Array,
    value: Array
  },
  directives: {
    focus: {
      inserted: function (el, event) {
        switch (event.value) {
          case 'checkbox':
            el.children[0].focus()
            break
          case 'date':
            el.children[0].focus()
            break
          default:
            el.focus()
            break
        }
      }
    },
    clickOutside: {
      bind: function (el, binding, vnode) {
        el.clickOutsideEvent = function (event) {
          if (!(el === event.target || el.contains(event.target))) {
            if (document.contains(event.target)) {
              vnode.context[binding.expression](event)
            }
          }
        }
        document.addEventListener('click', el.clickOutsideEvent)
      },
      unbind: function (el) {
        document.removeEventListener('click', el.clickOutsideEvent)
      }
    }
  },
  data () {
    return {
      selectedCell: {
        type: String,
        default: null
      },
      tableItems: this.value
        ? this.value.map((item) => ({ ...item, isEdit: false }))
        : this.items.map((item) => ({ ...item, isEdit: false }))
    }
  },
  watch: {
    value (newVal) {
      this.tableItems = this.createItems(newVal)
    },
    items (newVal) {
      this.tableItems = this.createItems(newVal)
    }
  },
  methods: {
    handleEditCell (e, index, name) {
      e.stopPropagation()
      this.resetItems()
      if (!this.tableItems[index]) {
        return
      }
      this.tableItems[index].isEdit = true
      this.selectedCell = name
    },
    handleKeydown (e, index, data) {
      if (e.code === 'Tab') {
        e.preventDefault()
        let fieldIndex = this.fields.length - 1 === index ? 0 : index + 1
        let rowIndex =
          this.fields.length - 1 === index ? data.index + 1 : data.index
        let i = fieldIndex
        // Find next editable field
        while (!this.fields[i].editable) {
          if (i === this.fields.length - 1) {
            i = 0
            rowIndex++
          } else {
            i++
          }
        }
        fieldIndex = i
        this.selectedCell = this.fields[fieldIndex].key
        this.resetItems()
        if (this.tableItems[rowIndex]) {
          this.tableItems[rowIndex].isEdit = true
        }
      } else if (e.code === 'Escape') {
        e.preventDefault()
        this.selectedCell = null
        this.resetItems()
      }
    },
    handleClickOut () {
      this.selectedCell = null
      this.resetItems()
    },
    inputHandler (value, data, key, options) {
      let changedValue = value
      // Handle select element with options
      if (options) {
        const selectedValue = options.find((item) => item.value === value)
        changedValue = selectedValue ? selectedValue.value : value
      }
      this.tableItems[data.index][key] = changedValue
      this.$emit('input-change', value, data)

      // If v-model is set then emit updated table
      if (this.value) {
        this.$emit(
          'input',
          this.tableItems.map((item) => {
            const newItem = { ...item }
            delete newItem.isEdit
            return newItem
          })
        )
      }
    },
    handleListeners (listeners) {
      // Exclude listeners that are not part of Bootstrap Vue
      const excludeEvents = {
        input: true,
        'input-change': true
      }
      return Object.keys(listeners).reduce(
        (a, c) =>
          excludeEvents[c] ? a : { ...a, [c]: listeners[c] },
        {}
      )
    },
    getValue (data) {
      let value = data.value
      // Handle select element with options
      if (data.field.options) {
        const selectedValue = data.field.options.find(
          (item) => item.value === value
        )
        value = selectedValue ? selectedValue.text : value
      }
      return value
    },
    resetItems () {
      this.tableItems = this.tableItems.map((item) => ({
        ...item,
        isEdit: false
      }))
    },
    createItems (value) {
      return value.map((item, index) => ({
        ...item,
        isEdit: this.tableItems[index] ? this.tableItems[index].isEdit : false
      }))
    }
  }
})
</script>

<style>
table.b-table {
  width: unset;
}
table.b-table td {
  padding: 0;
}
.data-cell {
  display: flex;
  width: 100%;
  height: 100%;
}
</style>



// WEBPACK FOOTER //
// src/components/EditableTable.vue